// ============================================================
// Profileur : mise en forme, en français, pour quelqu'un qui n'est pas développeur
// ============================================================
// Fonctions pures (testées par profiler.format.test.ts). Les nombres sont écrits à la main, virgule
// décimale et espace insécable pour les milliers, pour ne pas dépendre des données de langue du
// navigateur. Les heures sont celles du fuseau du navigateur ; `timeZone` ne sert qu'aux tests.
// ============================================================

const NBSP = ' ';

/** Nombre à `digits` décimales, virgule décimale, milliers séparés par une espace insécable. */
export function fmtNumber(value: number, digits = 0): string {
  if (!Number.isFinite(value)) return '-';
  const fixed = Math.abs(value).toFixed(digits);
  const [int, dec] = fixed.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  const zero = Number(fixed) === 0;
  return `${value < 0 && !zero ? '-' : ''}${grouped}${dec ? `,${dec}` : ''}`;
}

/**
 * Durée lisible : « 2,4 ms », « 697 ms », « 17,2 s », « 1 min 12 s ». Sous 10 ms une décimale compte
 * (le budget d'un tick est de 50 ms) ; au-delà d'une seconde, les millisecondes n'apprennent plus rien.
 * Une durée infime n'est pas arrondie à « 0,0 ms », qui se lirait « rien » : « moins de 0,1 ms ».
 */
export function fmtMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '-';
  if (ms === 0) return `0${NBSP}ms`;
  if (ms > 0 && ms < 0.05) return `moins de 0,1${NBSP}ms`;
  if (ms < 10) return `${fmtNumber(ms, 1)}${NBSP}ms`;
  if (ms < 1000) return `${fmtNumber(ms, 0)}${NBSP}ms`;
  if (ms < 60_000) return `${fmtNumber(ms / 1000, 1)}${NBSP}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms - minutes * 60_000) / 1000);
  return seconds > 0 ? `${minutes}${NBSP}min ${seconds}${NBSP}s` : `${minutes}${NBSP}min`;
}

/** Taille lisible, en puissances de 1 024 comme le moteur : « 512 o », « 12 Ko », « 1,6 Mo », « 2,1 Go ». */
export function fmtBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return '-';
  if (bytes < 1024) return `${fmtNumber(bytes, 0)}${NBSP}o`;
  if (bytes < 1024 ** 2) return `${fmtNumber(bytes / 1024, 0)}${NBSP}Ko`;
  if (bytes < 1024 ** 3) return `${fmtNumber(bytes / 1024 ** 2, 1)}${NBSP}Mo`;
  return `${fmtNumber(bytes / 1024 ** 3, 1)}${NBSP}Go`;
}

/** Part de 0 à 1 en pourcentage : « 83 % », et « moins de 1 % » plutôt qu'un zéro trompeur. */
export function fmtShare(share: number | null | undefined): string {
  if (share == null || !Number.isFinite(share)) return '-';
  const pct = share * 100;
  if (pct > 0 && pct < 1) return `moins de 1${NBSP}%`;
  return `${fmtNumber(pct, 0)}${NBSP}%`;
}

/** Ticks par seconde, une décimale : « 20,0 ». */
export function fmtTps(tps: number | null | undefined): string {
  return tps == null || !Number.isFinite(tps) ? '-' : fmtNumber(tps, 1);
}

/** « 1 pic », « 3 pics » : accord d'un nom régulier. */
export function plural(n: number, one: string, many: string): string {
  return `${fmtNumber(n, 0)}${NBSP}${n > 1 ? many : one}`;
}

/** Heure locale « 14:02 ». */
export function fmtClock(t: number, timeZone?: string): string {
  return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone }).format(t);
}

/** Heure locale à la seconde « 14:02:07 » : deux pics peuvent tomber dans la même minute. */
export function fmtClockSeconds(t: number, timeZone?: string): string {
  return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone }).format(t);
}

/** Jour local « 13/10 ». */
export function fmtDay(t: number, timeZone?: string): string {
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', timeZone }).format(t);
}

/** « 13/10 à 14:02 », ou « 14:02 » seul quand `t` tombe le même jour local que `now`. */
export function fmtWhen(t: number, now: number, timeZone?: string): string {
  return fmtDay(t, timeZone) === fmtDay(now, timeZone) ? fmtClock(t, timeZone) : `${fmtDay(t, timeZone)} à ${fmtClock(t, timeZone)}`;
}

/** Ancienneté : « à l'instant », « il y a 3 min », « il y a 2 h », « il y a 4 j ». */
export function fmtAgo(t: number, now: number): string {
  const ms = Math.max(0, now - t);
  if (ms < 90_000) return "à l'instant";
  if (ms < 3_600_000) return `il y a ${Math.round(ms / 60_000)}${NBSP}min`;
  if (ms < 48 * 3_600_000) return `il y a ${Math.round(ms / 3_600_000)}${NBSP}h`;
  return `il y a ${Math.round(ms / 86_400_000)}${NBSP}j`;
}

/** Période proposée, en clair. */
export function periodLabel(hours: number): string {
  if (hours === 1) return 'Dernière heure';
  if (hours === 24) return 'Dernières 24 heures';
  if (hours === 168) return '7 derniers jours';
  return `${hours} dernières heures`;
}

/** Largeur d'un point de la courbe, en clair : « par minute », « par tranche de 5 minutes ». */
export function stepLabel(stepSeconds: number): string {
  if (stepSeconds <= 60) return 'par minute';
  if (stepSeconds < 3600) return `par tranche de ${Math.round(stepSeconds / 60)} minutes`;
  return `par tranche de ${fmtNumber(stepSeconds / 3600, stepSeconds % 3600 === 0 ? 0 : 1)} heures`;
}

// ---------------------------------------------------------------- étapes du tick

interface StageText {
  /** Nom court, pour une ligne ou une légende. */
  label: string;
  /** Ce que fait cette étape, en une phrase, sans jargon. */
  help: string;
}

/**
 * Étapes et sous-étapes que le moteur sait nommer (Endium 0.1.48). Un nom inconnu (moteur plus récent)
 * s'affiche tel quel : mieux vaut un mot anglais exact qu'une traduction devinée.
 */
const STAGES: Record<string, StageText> = {
  Entities: { label: 'Entités', help: 'Joueurs, créatures, objets au sol, véhicules : tout ce qui bouge et vit dans le monde.' },
  ChunkTick: { label: 'Chunks', help: 'Les portions de carte : celles à charger ou à décharger, les zones gardées actives, et le tour de chacune (blocs qui évoluent, machines).' },
  ChunkTasks: { label: 'Tâches des chunks', help: 'Envoi aux joueurs des blocs qui ont changé, et travaux mis en file pour une portion de carte.' },
  Scheduler: { label: 'Tâches planifiées', help: 'Ce que les mods ont programmé pour plus tard : minuteries, actions différées.' },
  SyncPackets: { label: 'Actions des joueurs', help: 'Ce que les joueurs envoient au serveur : déplacements, clics, discussion.' },
  Lighting: { label: 'Lumière', help: 'Recalcul de la lumière quand un bloc change.' },
  Time: { label: 'Heure du monde', help: 'Avance du jour, de la nuit et de la météo.' },
  Coroutines: { label: 'Tâches longues des mods', help: 'Travaux que les mods étalent sur plusieurs ticks.' },
  Idle: { label: 'Entre deux ticks', help: 'Travail fait pendant que le monde attend le tick suivant. Ne compte pas dans la durée du tick.' },
  'Entities/tick': { label: 'Entités : mise à jour', help: 'Le tour de chaque entité : déplacement, intelligence, effets.' },
  'Entities/queue': { label: 'Entités : arrivées et départs', help: 'Entités ajoutées ou retirées du monde.' },
  'Entities/tasks': { label: 'Entités : tâches', help: 'Travaux demandés sur une entité depuis un autre fil.' },
  'Entities/autosave': { label: 'Entités : sauvegarde automatique', help: 'Écriture périodique des entités sur le disque.' },
  'ChunkTick/loaders': { label: 'Chunks : zones gardées chargées', help: 'Mise à jour des chargeurs de chunks (joueurs et mods qui gardent une zone active).' },
  'ChunkTick/holders': { label: 'Chunks : préparation', help: 'Chargement et déchargement des portions de carte.' },
  'ChunkTick/chunks': { label: 'Chunks : mise à jour', help: 'Le tour de chaque portion de carte : blocs qui évoluent, machines.' },
};

export function stageLabel(name: string | null | undefined): string {
  if (!name) return 'Étape inconnue';
  return STAGES[name]?.label ?? name;
}

export function stageHelp(name: string | null | undefined): string | null {
  return name ? STAGES[name]?.help ?? null : null;
}

// ---------------------------------------------------------------- cause d'un pic

export interface CauseText {
  /** Ce que la page dit : « Le monde attendait ». */
  label: string;
  /** L'explication, en une ou deux phrases. */
  help: string;
}

/** Codes de cause du satellite (ProfilerCause). Un code inconnu est traité comme « unknown ». */
const CAUSES: Record<string, CauseText> = {
  waiting: {
    label: 'Le monde attendait',
    help: "Pendant ce tick, le serveur n'a presque pas calculé : il attendait autre chose (un verrou, le disque, ou la machine occupée ailleurs).",
  },
  computing: {
    label: 'Le monde calculait',
    help: 'Le serveur a travaillé pendant tout le tick : trop de choses à faire en une seule fois.',
  },
  busyLoop: {
    label: 'Le monde tournait en boucle',
    help: "Plus d'une seconde de calcul dans une seule étape, presque sans rien produire : une boucle qui ne finissait pas.",
  },
  gc: {
    label: 'Nettoyage de la mémoire',
    help: 'Le nettoyage automatique de la mémoire a mis le monde en pause pendant plus de la moitié du tick.',
  },
  allocation: {
    label: "Trop de mémoire prise d'un coup",
    help: "Ce tick n'était pas forcément long, mais il a réservé plus de mémoire que le seuil fixé sur ce serveur.",
  },
  unknown: {
    label: 'Cause non déterminée',
    help: 'Les mesures de ce tick ne désignent pas de cause nette.',
  },
};

export function causeText(cause: string | null | undefined): CauseText {
  return CAUSES[cause ?? 'unknown'] ?? CAUSES.unknown;
}

/**
 * Nom d'un fil du serveur rendu lisible, sans le déformer : le nom d'origine reste dans l'infobulle.
 * Le fil du ramasse-miettes garde en plus son nom technique à l'écran, entre parenthèses : c'est sous
 * ce nom qu'on le retrouve dans la documentation de .NET et dans les autres outils.
 */
export function threadLabel(name: string): string {
  const level = /^Level Thread #(.+)$/.exec(name);
  if (level) return `Monde ${level[1]}`;
  const asyncLevel = /^AsyncLevel Thread #(.+)$/.exec(name);
  if (asyncLevel) return `Monde ${asyncLevel[1]} (tâches de fond)`;
  const network = /^Endium Network (Boss|Child) #(\d+)$/.exec(name);
  if (network) return `Réseau ${network[1] === 'Boss' ? 'principal' : 'secondaire'} n° ${network[2]}`;
  if (name === 'Console') return 'Console du serveur';
  if (name === 'Endium Profiler') return 'Profileur';
  if (name === '.NET TP Worker') return 'Tâches générales';
  if (name === '.NET Server GC') return 'Nettoyage de la mémoire (.NET Server GC)';
  if (name === 'unnamed' || name.trim() === '') return 'Fil sans nom';
  return name;
}
